import '@salesforce/canvas-js-sdk'
import axios from 'axios'
import type {
  SalesforceRecord,
  SalesforceOrganization,
  CustomParameters,
  CanvasClient,
} from '../../externalApps/SalesforceApp/types/salesforce'

/**
 * Canvas Authentication Service
 * Manages JWT tokens for Salesforce Canvas authentication
 * - Exchanges short-lived Exchange JWT for Access JWT
 * - Auto-refreshes tokens based on server-provided expiry time
 * - Stores tokens in memory only (no localStorage/sessionStorage)
 *
 * DEV MODE:
 * - When VITE_SALESFORCE_USE_AUTH0=true, bypasses Canvas backend
 * - Uses Auth0 token directly from auth context
 * - Skips token exchange and auto-refresh
 */
class CanvasAuthService {
  // Private fields (in-memory storage only)
  #accessToken: string | null = null
  #partnerId: string | null = null
  #crmId: string | null = null
  #refreshTimer: NodeJS.Timeout | null = null
  #isInitialized: boolean = false
  #useAuth0: boolean = import.meta.env.VITE_SALESFORCE_USE_AUTH0 === 'true'
  #accessTokenExpirySeconds: number = 900 // Default: 15 minutes, updated from server response
  #record: SalesforceRecord | undefined = undefined
  #organization: SalesforceOrganization | null = null
  #locationUrl: string | null = null
  #canvasClient: CanvasClient | null = null
  #canvasId: string | null = null
  #customParameters: CustomParameters | null = null

  constructor() {
    // In dev mode (Auth0), initialize Salesforce context from environment variables
    // All fields are optional - set only what you need for testing
    if (this.#useAuth0) {
      // Load partnerId (optional)
      this.#partnerId = import.meta.env.VITE_SALESFORCE_DEV_PARTNER_ID || null

      // Load crmId (optional - for dev mode)
      this.#crmId = import.meta.env.VITE_SALESFORCE_DEV_CRM_ID || null

      // Load organization (optional)
      const orgId = import.meta.env.VITE_SALESFORCE_DEV_ORG_ID
      const instanceUrl = import.meta.env.VITE_SALESFORCE_DEV_INSTANCE_URL || ''
      if (orgId) {
        this.#organization = { Id: orgId }
      }

      // Set dev canvasClient with instanceUrl so getInstanceUrl() works in dev mode
      if (instanceUrl) {
        this.#canvasClient = {
          oauthToken: '',
          instanceId: '',
          instanceUrl,
          targetOrigin: '',
        }
      }

      // Load record (optional - only if both values provided)
      const recordId = import.meta.env.VITE_SALESFORCE_DEV_RECORD_ID
      const recordApiName = import.meta.env.VITE_SALESFORCE_DEV_RECORD_API_NAME
      if (recordId && recordApiName) {
        this.#record = { recordId, objectAPIName: recordApiName }
      }

      // Load locationUrl (optional - for postMessage testing)
      this.#locationUrl =
        import.meta.env.VITE_SALESFORCE_DEV_TARGET_ORIGIN || null
    }
  }

  /**
   * Initialize the auth service
   * - Exchanges Exchange JWT for Access JWT
   * - Starts auto-refresh timer based on token expiry
   *
   * DEV MODE: Skips all steps if using Auth0
   */
  async initialize(): Promise<void> {
    if (this.#isInitialized) {
      return
    }

    // DEV MODE: Skip Canvas backend initialization
    if (this.#useAuth0) {
      console.log('🔧 Dev Mode: Skipping Canvas authentication (using Auth0)')
      this.#isInitialized = true
      return
    }

    try {
      // Step 1: Exchange Exchange JWT for Access JWT
      await this.exchangeToken()

      // Step 2: Start auto-refresh timer based on token expiry
      this.startAutoRefresh()

      this.#isInitialized = true
    } catch (error) {
      throw new Error('Authentication initialization failed')
    }
  }

  /**
   * Get Exchange token from sessionStorage
   */
  getExchangeTokenFromSessionStorage(): string | null {
    return sessionStorage.getItem('exchange_token')
  }

  /**
   * Get raw signed request string from Salesforce via refreshSignedRequest
   */
  private getRawSignedRequest(): Promise<string> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(
          new Error('Timed out waiting for Salesforce refreshSignedRequest')
        )
      }, 10_000)

      try {
        Sfdc.canvas.client.refreshSignedRequest(data => {
          clearTimeout(timeout)
          if (data.status === 200 && data.payload?.response) {
            resolve(data.payload.response)
          } else {
            reject(
              new Error(
                `refreshSignedRequest failed with status ${data.status}`
              )
            )
          }
        })
      } catch (err) {
        clearTimeout(timeout)
        reject(err)
      }
    })
  }

  /**
   * Decode oauthToken, instanceId, and refreshToken from a raw signed request payload
   * Note: Payload is decoded client-side for local use only.
   * Signature verification is performed by the backend when the signed request is sent to the refresh endpoint.
   */
  private decodeCanvasClientFromSignedRequest(rawSignedRequest: string): {
    oauthToken: string
    instanceId: string
    refreshToken?: string | null
  } {
    const parts = rawSignedRequest.split('.')
    const decoded = JSON.parse(atob(parts[1]))
    const oauthToken = decoded?.client?.oauthToken
    const instanceId = decoded?.client?.instanceId
    const refreshToken = decoded?.client?.refreshToken ?? null
    if (!oauthToken || !instanceId) {
      throw new Error('Missing oauthToken or instanceId in signed request')
    }
    return { oauthToken, instanceId, refreshToken }
  }

  /**
   * Exchange short-lived Exchange JWT for Access JWT (one-time use)
   */
  private async exchangeToken(): Promise<void> {
    try {
      const response = await axios.post(
        '/backend/salesforce/canvas/token',
        {},
        {
          withCredentials: true,
          headers: {
            'X-Exchange-Token': this.getExchangeTokenFromSessionStorage(),
          },
        }
      )

      this.#accessToken = response.data.accessToken
      this.#partnerId = response.data.partnerId
      this.#crmId = response.data.crmId
      this.#accessTokenExpirySeconds = response.data.expiresIn || 900
      this.#record = response.data.record
      this.#organization = response.data.organization
      this.#locationUrl = response.data.locationUrl
      this.#canvasId = response.data.canvasId || null
      this.#customParameters = response.data.customParameters || null

      const serverClient = response.data.canvasClient
      if (serverClient) {
        const rawSignedRequest = await this.getRawSignedRequest()
        const { oauthToken, instanceId, refreshToken } =
          this.decodeCanvasClientFromSignedRequest(rawSignedRequest)
        this.#canvasClient = {
          ...serverClient,
          oauthToken,
          instanceId,
          refreshToken,
        }
      } else {
        this.#canvasClient = null
      }

      sessionStorage.removeItem('exchange_token')
    } catch (error: any) {
      console.error('❌ Token exchange failed:', { cause: error })
      if (error.response?.status === 401) {
        // Exchange JWT expired or invalid - redirect to re-authenticate
        this.handleSessionExpiry()
      }

      throw error
    }
  }

  /**
   * Refresh access token (get new Access JWT)
   * DEV MODE: No-op (Auth0 token refresh handled by Auth0)
   */
  async refreshToken(): Promise<void> {
    if (this.#useAuth0) return

    try {
      const rawSignedRequest = await this.getRawSignedRequest()

      const response = await axios.post(
        '/backend/salesforce/canvas/token/refresh',
        { signed_request: rawSignedRequest },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${this.#accessToken}`,
          },
        }
      )

      this.#accessToken = response.data.accessToken
      this.#accessTokenExpirySeconds = response.data.expiresIn || 900

      // Update canvasClient with fresh oauthToken/instanceId/refreshToken
      if (this.#canvasClient) {
        const { oauthToken, instanceId, refreshToken } =
          this.decodeCanvasClientFromSignedRequest(rawSignedRequest)
        this.#canvasClient = {
          ...this.#canvasClient,
          oauthToken,
          instanceId,
          refreshToken,
        }
      }
    } catch (error: any) {
      console.error('❌ Token refresh failed:', { cause: error })
      if (error.response?.status === 401) {
        this.handleSessionExpiry()
      }
      throw error
    }
  }

  /**
   * Get current access token
   * DEV MODE: Returns Auth0 token from localStorage
   */
  getAccessToken(): string | null {
    // DEV MODE: Return Auth0 token from localStorage
    if (this.#useAuth0) {
      try {
        return localStorage.getItem('auth0-access-token')
      } catch (error) {
        console.error('❌ Failed to get Auth0 token from localStorage:', error)
        return null
      }
    }

    return this.#accessToken
  }

  /**
   * Get partner ID
   */
  getPartnerId(): string | null {
    return this.#partnerId
  }

  /**
   * Get CRM ID
   */
  getCrmId(): string | null {
    return this.#crmId
  }

  /**
   * Get record information (optional - only available when launched from record context)
   */
  getRecord(): SalesforceRecord | undefined {
    return this.#record
  }

  /**
   * Get organization information
   */
  getOrganization(): SalesforceOrganization | null {
    return this.#organization
  }

  /**
   * Get location URL for secure postMessage communication
   */
  getLocationUrl(): string | null {
    return this.#locationUrl
  }

  /**
   * Get Salesforce instance URL for building navigation URLs
   */
  getInstanceUrl(): string | null {
    return this.#canvasClient?.instanceUrl ?? null
  }

  /**
   * Get Canvas client object for Canvas SDK communication
   */
  getCanvasClient(): CanvasClient | null {
    return this.#canvasClient
  }

  /**
   * Get canvas ID for secure postMessage validation
   */
  getCanvasId(): string | null {
    return this.#canvasId
  }

  /**
   * Get custom parameters passed from Salesforce Canvas
   */
  getCustomParameters(): CustomParameters | null {
    return this.#customParameters
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.#isInitialized
  }

  /**
   * Start auto-refresh timer
   * Refreshes 60 seconds before token expiry to ensure uninterrupted access
   */
  private startAutoRefresh(): void {
    // Clear existing timer if any
    if (this.#refreshTimer) {
      clearInterval(this.#refreshTimer)
    }

    // Refresh 60 seconds before token expiry (or at 80% of expiry time, whichever is sooner)
    const bufferSeconds = Math.min(
      60,
      Math.floor(this.#accessTokenExpirySeconds * 0.2)
    )
    const refreshInterval =
      (this.#accessTokenExpirySeconds - bufferSeconds) * 1000

    this.#refreshTimer = setInterval(async () => {
      try {
        await this.refreshToken()
      } catch (error) {
        console.error('❌ Auto-refresh failed:', error)
        this.stopAutoRefresh()
      }
    }, refreshInterval)
  }

  /**
   * Stop auto-refresh timer
   */
  private stopAutoRefresh(): void {
    if (this.#refreshTimer) {
      clearInterval(this.#refreshTimer)
      this.#refreshTimer = null
    }
  }

  /**
   * Handle session expiry
   */
  private handleSessionExpiry(): void {
    this.stopAutoRefresh()
    this.#accessToken = null
    this.#partnerId = null
    this.#crmId = null
    this.#record = undefined
    this.#organization = null
    this.#locationUrl = null
    this.#canvasClient = null
    this.#canvasId = null
    this.#customParameters = null
    this.#isInitialized = false
  }

  /**
   * Logout (destroy session)
   * DEV MODE: No-op (Auth0 logout handled separately)
   */
  async logout(): Promise<void> {
    // DEV MODE: No logout needed with Auth0
    if (this.#useAuth0) {
      this.#isInitialized = false
      return
    }

    try {
      await axios.post(
        '/backend/salesforce/canvas/logout',
        {},
        {
          withCredentials: true,
          headers: {},
        }
      )

      this.stopAutoRefresh()
      this.#accessToken = null
      this.#partnerId = null
      this.#crmId = null
      this.#record = undefined
      this.#organization = null
      this.#locationUrl = null
      this.#canvasClient = null
      this.#canvasId = null
      this.#customParameters = null
      this.#isInitialized = false
    } catch (error) {
      throw error
    }
  }
}

// Export singleton instance
export const canvasAuthService = new CanvasAuthService()
